import { put, call, takeLatest, select } from 'redux-saga/effects';
import { push, replace } from 'connected-react-router';
import Notify, { AlertTypes } from 'Services/notification';
import authErrorResolve from 'Services/auth/errors/authError.resolve';
import StorageService from 'Services/storage';
import AuthService from 'Services/auth';
import UserService from 'Services/user';
import { chatIdentify, chatLogout } from 'Services/chat';
import EventService from 'Services/events';
import i18n from 'Services/i18n';
import {
  setAuthenticationAction,
  setIsAuthenticatedAction,
  resetStoreAction,
  setUserInfoAction,
} from 'Store/modules/auth/actions';
import {
  hideModalAction,
  setAuthErrorAction,
  setIsLoadingAction,
  setIsNotLoadingAction,
} from 'Store/modules/app/actions';
import {
  codeSelector,
  couponSelector,
  isAuthenticatedSelector,
  sourceSelector,
  userAuthSelector,
} from 'Store/modules/auth/selectors';
import * as types from 'Store/modules/auth/types';
import { routeStateSelector, searchSelector } from 'Store/modules/router/selectors';
import {
  resetAnalytics,
  setIdentify,
  initSessionReplay,
  shutdownSessionReplay,
} from 'Utils/analytics';
import { routes } from 'Constants/routes';
import { Providers } from 'Types/common';
import { stopPollingAction } from '../core/actions';

export function* handleAuthError({ error, action, showNotif = true }) {
  const CognitoError = authErrorResolve(error.code);
  const handleError = new CognitoError();
  if (handleError.hasAction) {
    yield call(handleError.action, { ...action.payload });
    return;
  }
  if (showNotif) {
    yield call(Notify.sendNotification, i18n.t(`error:${handleError.message}`), AlertTypes.error);
  }
}

export function* resetUser() {
  yield call(StorageService.clearStorage);
  yield put(resetStoreAction());
  yield call(resetAnalytics);
  yield call(shutdownSessionReplay);
  yield call(chatLogout);
  yield call(EventService.userSignOutEvent);
  yield call(AuthService.signOut);
  yield put(stopPollingAction());
}

export function* setIdentityId({ email, phone, userId, userHashIntercom }) {
  yield call(chatIdentify, userId, userHashIntercom);
  yield call(setIdentify, { userId, email, phone });
  yield call(initSessionReplay);
}

export function* signUpSaga(action) {
  const { email, password, coupon } = action.payload;
  const defaultCode = yield select(codeSelector);
  try {
    yield put(setIsLoadingAction());
    yield call(AuthService.signUp, { username: email, password, coupon, code: defaultCode });
    yield call(AuthService.signIn, { username: email, password });
    yield put(setAuthenticationAction());
  } catch (error) {
    yield call(handleAuthError, { error });
  } finally {
    yield put(setIsNotLoadingAction());
  }
}

export function* signUpSetEmailSaga(action) {
  const { email } = action.payload;
  try {
    yield put(setIsLoadingAction());
    const response = yield call(AuthService.checkEmail, email);
    const provider = response?.provider || Providers.TAXDOWN;
    if (response?.provider) {
      yield put(setAuthErrorAction({ message: `CheckEmail.accountExistError.${provider}` }));
    } else if (!response?.domainAllowed && !response?.emailVerified) {
      yield put(setUserInfoAction({ user: { email } }));
      yield put(setAuthErrorAction({ message: 'CheckEmail.invalidProvider', type: 'warning' }));
    } else if (response?.domainAllowed && !response?.emailVerified) {
      yield put(setUserInfoAction({ user: { email } }));
      yield put(push(routes.SIGNUP_STEP_TWO));
    }
  } catch (error) {
    if (error.status === 422) {
      yield put(setAuthErrorAction({ message: 'CheckEmail.validEmailError' }));
    }
  } finally {
    yield put(setIsNotLoadingAction());
  }
}

export function* confirmSignUpSaga(action) {
  const { token, ref } = action.payload;
  const isAuthenticated = yield select(isAuthenticatedSelector);
  try {
    if (isAuthenticated) {
      yield call(resetUser);
    }
    yield put(setIsLoadingAction());
    yield call(AuthService.verifyEmail, token);
    yield call(EventService.userVerifyEmail);
    yield call(
      Notify.sendNotification,
      i18n.t('auth:ConfirmSignUp.Tooltip.success'),
      AlertTypes.success
    );
    if (ref) {
      yield put(push(routes.SIGNIN));
    } else {
      yield put(setAuthenticationAction({ isSignUp: true }));
    }
  } catch (error) {
    const errorCode = error?.status;
    // TODO
    // Move to problem+json handler
    if (errorCode === 409) {
      yield call(
        Notify.sendNotification,
        i18n.t('auth:ConfirmSignUp.EmailAlreadyVerified'),
        AlertTypes.error
      );
      yield put(push(routes.SIGNIN));
    } else {
      yield call(handleAuthError, { error });
    }
  } finally {
    yield put(setIsNotLoadingAction());
  }
}

export function* resendConfirmationCodeSaga(action) {
  const { email } = action.payload;
  try {
    yield put(setIsLoadingAction());
    yield call(AuthService.sendVerifyEmail, email);
    yield call(
      Notify.sendNotification,
      i18n.t('auth:AccountValidation.Tooltip.resend', { email }),
      AlertTypes.success
    );
    yield call(EventService.userRequestedEmail);
  } catch (error) {
    if (error.status === 429) {
      yield put(setAuthenticationAction());
    } else {
      yield call(handleAuthError, { error });
    }
  } finally {
    yield put(setIsNotLoadingAction());
  }
}

export function* VerificationCodeSignUpSaga(action) {
  const { code, email } = action.payload;
  const passwordInfo = yield select(routeStateSelector);
  try {
    yield put(setIsLoadingAction());
    yield call(AuthService.confirmSignUp, { username: email, code });
    yield call(
      Notify.sendNotification,
      'Hemos validado correctamente tu cuenta',
      AlertTypes.success
    );
    yield call(EventService.userVerifyEmail);
    if (!passwordInfo) {
      return yield put(push(routes.SIGNIN));
    }
    const { password } = passwordInfo;
    yield call(AuthService.signIn, { username: email, password });
    yield put(setAuthenticationAction({ isSignUp: true }));
  } catch (error) {
    yield call(handleAuthError, { error });
  } finally {
    yield put(setIsNotLoadingAction());
  }
}

export function* ResendVerificationCodeSignUpSaga(action) {
  const { email } = action.payload;
  try {
    yield put(setIsLoadingAction());
    yield call(AuthService.resendConfirmSignUp, { username: email });
    yield call(
      Notify.sendNotification,
      i18n.t('auth:Auth.resendPassword.notify'),
      AlertTypes.success
    );
  } catch (error) {
    yield call(handleAuthError, { error });
  } finally {
    yield put(setIsNotLoadingAction());
  }
}

export function* checkEmailSignInSaga(email) {
  let hasError = false;
  try {
    const response = yield call(AuthService.checkEmail, email);
    const { provider, emailVerified } = response;
    if (!provider) {
      yield put(setAuthErrorAction({ message: 'SignInEmail.notAccountError' }));
      hasError = true;
    } else if (emailVerified && provider !== Providers.TAXDOWN) {
      yield put(
        setAuthErrorAction({ message: `CheckEmail.accountExistError.${response.provider}` })
      );
      hasError = true;
    }
  } catch (error) {
    if (error.status === 406) {
      yield put(setAuthErrorAction({ message: 'CheckEmail.accountNotExistError' }));
    }
    hasError = true;
  }
  return hasError;
}

export function* signInSaga(action) {
  const { email, password } = action.payload;
  try {
    yield put(setIsLoadingAction());
    const hasError = yield call(checkEmailSignInSaga, email);
    if (!hasError) {
      yield call(AuthService.signIn, { username: email, password });
      yield put(setAuthenticationAction());
    }
  } catch (error) {
    yield put(setAuthErrorAction({ message: 'SignIn.EmailUserError' }));
    yield call(handleAuthError, { error, action, showNotif: false });
  } finally {
    yield put(setIsNotLoadingAction());
  }
}

export function* sendAuthEvents(email, isNew, isVerified) {
  if (isNew) {
    yield call(EventService.userSignUpEvent, email);
  }
  if (isVerified) {
    yield call(EventService.userSignInEndEvent);
  }
}

export function* authRedirectSaga({
  emailVerified,
  email,
  socialUser,
  redirectUri,
  isSignUp,
  isNewUser,
}) {
  yield call(sendAuthEvents, email, isNewUser, emailVerified || socialUser);
  if (!emailVerified && !socialUser) {
    yield call(AuthService.sendVerifyEmail, email);
    yield put(push(routes.ACCOUNT_VALIDATION, email));
  } else if (isSignUp || isNewUser) {
    yield put(setIsAuthenticatedAction(true));
    yield put(replace(routes.AUTH_ONBOARDING));
  } else {
    yield put(setIsAuthenticatedAction(true));
    yield put(replace(redirectUri || routes.DASHBOARD));
  }
}

export function* setAuthenticationSaga(action) {
  const { isSignUp, withSocialProvider } = action.payload;
  const defaultCoupon = yield select(couponSelector);
  const defaultCode = yield select(codeSelector);
  const user = yield select(userAuthSelector);
  const source = yield select(sourceSelector);
  try {
    if (!user && !withSocialProvider) {
      yield put(push(routes.SIGNIN));
      return;
    }
    yield put(setIsLoadingAction());
    const response = yield call(AuthService.currentUser, { cached: false });
    const socialUser = response?.signInUserSession?.idToken?.payload?.identities;
    const {
      userId,
      userHashIntercom,
      phone,
      email,
      coupon,
      newUser,
      emailVerified,
      code,
      ...rest
    } = yield call(UserService.getUserId);
    const isNewUser = newUser;
    const newCoupon = !coupon && defaultCoupon ? defaultCoupon : coupon;
    const newCode = !code && defaultCode ? defaultCode : code;
    const userState = {
      user: {
        id: userId,
        userHash: userHashIntercom,
        email,
        phone,
        isSocial: !!socialUser,
        coupon: newCoupon,
        code: newCode,
        provider: socialUser ? socialUser[0].providerType : Providers.TAXDOWN,
        ...rest,
      },
    };
    yield call(setIdentityId, { email, userId, phone, userHashIntercom });
    yield put(setUserInfoAction(userState));
    if (defaultCoupon || defaultCode) {
      yield call(UserService.setUserDiscountInfo, {
        coupon: defaultCoupon,
        referralCode: defaultCode,
      });
    }
    yield call(authRedirectSaga, { emailVerified, email, socialUser, isSignUp, isNewUser, source });
  } catch (error) {
    yield call(handleAuthError, { error });
  } finally {
    yield put(setIsNotLoadingAction());
  }
}

export function* forgotPasswordSaga(action) {
  const { email } = action.payload;
  try {
    yield put(setIsLoadingAction());
    yield call(AuthService.forgotPassword, email);
    yield call(
      Notify.sendNotification,
      i18n.t('auth:EmailValidation.success', { email }),
      AlertTypes.success
    );
    yield put(push(routes.EMAIL_VALIDATION, email));
  } catch (error) {
    if (error.code === 'InvalidParameterException') {
      yield call(AuthService.sendVerifyEmail, email, 'forgotPassword');
      yield call(
        Notify.sendNotification,
        i18n.t('auth:AccountValidation.Tooltip.success', { email }),
        AlertTypes.success
      );
      yield put(push(routes.ACCOUNT_VALIDATION, email));
    } else {
      yield put(setAuthErrorAction({ message: 'SignInEmail.notAccountError' }));
      yield call(handleAuthError, { error, showNotif: false });
    }
  } finally {
    yield put(setIsNotLoadingAction());
  }
}

export function* resendForgotPasswordLinkSaga(action) {
  const { email } = action.payload;
  try {
    yield put(setIsLoadingAction());
    yield call(AuthService.forgotPassword, email);
    yield call(
      Notify.sendNotification,
      i18n.t('auth:AccountValidation.Tooltip.resend', { email }),
      AlertTypes.success
    );
  } catch (error) {
    yield call(handleAuthError, { error });
  } finally {
    yield put(setIsNotLoadingAction());
  }
}

export function* resetPasswordSaga(action) {
  const { email, password, validationCode } = action.payload;
  try {
    yield put(setIsLoadingAction());
    yield call(AuthService.resetPassword, { username: email, code: validationCode, password });
    yield call(Notify.sendNotification, i18n.t('auth:ResetPassword.success'), AlertTypes.success);
    yield call(AuthService.signIn, { username: email, password });
    yield put(setAuthenticationAction());
  } catch (error) {
    yield call(handleAuthError, { error });
  } finally {
    yield put(setIsNotLoadingAction());
  }
}

export function* resendResetPassCodeAction(action) {
  const { email } = action.payload;
  try {
    yield put(setIsLoadingAction());
    yield call(AuthService.forgotPassword, { username: email });
    yield call(
      Notify.sendNotification,
      'Hemos enviado un código nuevo. Compruebe su correo',
      AlertTypes.success
    );
  } catch (error) {
    yield call(handleAuthError, { error });
  } finally {
    yield put(setIsNotLoadingAction());
  }
}

export function* updatePasswordSaga(action) {
  const { oldPassword, newPassword } = action.payload;
  try {
    yield put(setIsLoadingAction());
    const user = yield call(AuthService.currentUser, { cached: false });
    yield call(AuthService.updatePassword, { user, oldPassword, newPassword });
    yield call(
      Notify.sendNotification,
      'Tu contraseña se ha actualizado correctamente.',
      AlertTypes.success
    );
    yield put(hideModalAction());
  } catch (error) {
    yield call(handleAuthError, { error });
  } finally {
    yield put(setIsNotLoadingAction());
  }
}

export function* signInSocialSaga(action) {
  const { provider } = action.payload;
  try {
    yield put(setIsLoadingAction());
    yield call(AuthService.signInSocial, provider);
  } catch (error) {
    yield call(handleAuthError, { error });
  } finally {
    yield put(setIsNotLoadingAction());
  }
}

export function* signOutSaga() {
  const search = yield select(searchSelector);
  yield put(setIsLoadingAction());
  yield call(resetUser);
  yield put(push({ pathname: routes.SIGNIN, search }));
  yield put(setIsNotLoadingAction());
}

export function* watchAuthSaga() {
  yield takeLatest(types.VERIFICATION_CODE_SIGNUP, VerificationCodeSignUpSaga);
  yield takeLatest(types.RESEND_VERIFICATION_CODE_SIGNUP, ResendVerificationCodeSignUpSaga);
  yield takeLatest(types.SIGN_IN_TYPE, signInSaga);
  yield takeLatest(types.SIGN_UP, signUpSaga);
  yield takeLatest(types.SIGN_UP_SET_EMAIL, signUpSetEmailSaga);
  yield takeLatest(types.CONFIRM_SIGN_UP, confirmSignUpSaga);
  yield takeLatest(types.RESEND_CONFIRMATION_CODE, resendConfirmationCodeSaga);
  yield takeLatest(types.RESEND_LINK, resendForgotPasswordLinkSaga);
  yield takeLatest(types.SIGN_IN_SOCIAL, signInSocialSaga);
  yield takeLatest(types.SET_AUTHENTICATION, setAuthenticationSaga);
  yield takeLatest(types.FORGOT_PASSWORD, forgotPasswordSaga);
  yield takeLatest(types.RESET_PASSWORD, resetPasswordSaga);
  yield takeLatest(types.RESEND_RESET_PASSWORD_CODE, resendResetPassCodeAction);
  yield takeLatest(types.UPDATE_PASSWORD, updatePasswordSaga);
  yield takeLatest(types.SIGN_OUT, signOutSaga);
}
